import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import BalanceRoundedIcon from "@mui/icons-material/BalanceRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { ownerFinanceStatsApi } from "../../../../api/ownerFinanceStats";
import { formatApiError } from "../../../../api/client";
import OwnerFinanceHistoryChart from "./OwnerFinanceHistoryChart";

const RANGE_OPTIONS = [
  { value: "all", label: "Todo el histórico" },
  { value: "24m", label: "Últimos 24 meses" },
  { value: "12m", label: "Últimos 12 meses" },
  { value: "90d", label: "Últimos 90 días" },
  { value: "30d", label: "Últimos 30 días" },
];

function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

function rangeParams(range) {
  const end = new Date();
  const start = new Date(end);
  if (range === "all") return { start: "", end: isoDate(end), autoBucket: "month" };
  if (range === "24m") { start.setMonth(start.getMonth() - 24); return { start: isoDate(start), end: isoDate(end), autoBucket: "month" }; }
  if (range === "12m") { start.setMonth(start.getMonth() - 12); return { start: isoDate(start), end: isoDate(end), autoBucket: "month" }; }
  if (range === "90d") { start.setDate(start.getDate() - 90); return { start: isoDate(start), end: isoDate(end), autoBucket: "week" }; }
  start.setDate(start.getDate() - 30);
  return { start: isoDate(start), end: isoDate(end), autoBucket: "day" };
}

function validDateKey(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

function money(value, currency) {
  try {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency, maximumFractionDigits: currency === "ARS" ? 0 : 2 }).format(Number(value || 0));
  } catch {
    return `${currency} ${Number(value || 0).toLocaleString("es-AR")}`;
  }
}

function Kpi({ label, value, helper, icon, tone = "primary" }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.1, borderRadius: 3, minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "text.secondary", fontWeight: 850, fontSize: 13 }}>{label}</Typography>
          <Typography sx={{ mt: .45, fontSize: { xs: 22, xl: 28 }, lineHeight: 1.05, fontWeight: 1000, letterSpacing: "-.035em", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</Typography>
          <Typography sx={{ mt: .7, color: "text.secondary", fontWeight: 650, fontSize: 12.5 }}>{helper}</Typography>
        </Box>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: `${tone}.main`, color: `${tone}.contrastText`, flexShrink: 0 }}>{icon}</Box>
      </Stack>
    </Paper>
  );
}

export default function OwnerFinanceHistoricalView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const jarvisCategory = String(searchParams.get("categoria") || "").trim();
  const jarvisStart = String(searchParams.get("desde") || "").trim();
  const jarvisEnd = String(searchParams.get("hasta") || "").trim();
  const hasJarvisRange = validDateKey(jarvisStart) && validDateKey(jarvisEnd);
  const [accounts, setAccounts] = React.useState([]);
  const [range, setRange] = React.useState(hasJarvisRange ? "custom" : "all");
  const [currency, setCurrency] = React.useState(String(searchParams.get("moneda") || ""));
  const [accountId, setAccountId] = React.useState(String(searchParams.get("cuenta") || ""));
  const [bucket, setBucket] = React.useState(hasJarvisRange ? "day" : "month");
  const [metric, setMetric] = React.useState("cumulative");
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    const nextCurrency = String(searchParams.get("moneda") || "");
    const nextAccountId = String(searchParams.get("cuenta") || "");
    const nextStart = String(searchParams.get("desde") || "");
    const nextEnd = String(searchParams.get("hasta") || "");
    if (nextCurrency) setCurrency(nextCurrency);
    setAccountId(nextAccountId);
    setRange((current) => validDateKey(nextStart) && validDateKey(nextEnd) ? "custom" : (current === "custom" ? "all" : current));
  }, [searchParams]);

  React.useEffect(() => {
    ownerFinanceStatsApi.accounts().then((response) => {
      const next = response.accounts || [];
      setAccounts(next);
      const currencies = [...new Set(next.map((a) => a.currency).filter(Boolean))];
      setCurrency((current) => current || (currencies.includes("ARS") ? "ARS" : currencies[0] || "ARS"));
    }).catch(() => setCurrency((current) => current || "ARS"));
  }, []);

  React.useEffect(() => {
    if (range === "custom") {
      setBucket("day");
      return;
    }
    const { autoBucket } = rangeParams(range);
    setBucket(autoBucket);
  }, [range]);

  React.useEffect(() => {
    if (!currency) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const selectedRange = range === "custom" && hasJarvisRange
          ? { start: jarvisStart, end: jarvisEnd }
          : rangeParams(range);
        const response = await ownerFinanceStatsApi.history({
          start: selectedRange.start,
          end: selectedRange.end,
          currency,
          accountId,
          category: jarvisCategory,
          bucket,
        });
        if (!cancelled) setStats(response.stats);
      } catch (err) {
        if (!cancelled) setError(formatApiError(err, "No pude cargar las estadísticas financieras."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [range, currency, accountId, bucket, jarvisCategory, jarvisStart, jarvisEnd, hasJarvisRange]);

  const currencies = React.useMemo(() => {
    const values = [...new Set(accounts.map((a) => a.currency).filter(Boolean))];
    if (currency && !values.includes(currency)) values.push(currency);
    return values.length ? values : ["ARS"];
  }, [accounts, currency]);
  const currencyAccounts = accounts.filter((account) => !currency || account.currency === currency);
  const summary = stats?.summary || {};
  const bucketCount = Math.max(stats?.series?.length || 0, 1);
  const avgNet = Number(summary.net || 0) / bucketCount;
  const maxCategory = Math.max(...(stats?.topExpenseCategories || []).map((item) => item.amount), 1);

  const changeRange = (nextRange) => {
    setRange(nextRange);
    if (nextRange !== "custom" && hasJarvisRange) {
      const params = new URLSearchParams(searchParams);
      params.delete("desde");
      params.delete("hasta");
      setSearchParams(params, { replace: true });
    }
  };

  const clearJarvisCategory = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("categoria");
    setSearchParams(params, { replace: true });
  };

  return (
    <Stack spacing={1.7}>
      <Paper variant="outlined" sx={{ p: 1.35, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.15} alignItems={{ lg: "center" }}>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Período</InputLabel>
            <Select value={range} label="Período" onChange={(e) => changeRange(e.target.value)}>
              {range === "custom" ? <MenuItem value="custom">Rango indicado por Jarvis</MenuItem> : null}
              {RANGE_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Moneda</InputLabel>
            <Select value={currency} label="Moneda" onChange={(e) => { setCurrency(e.target.value); setAccountId(""); }}>
              {currencies.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 210 }}>
            <InputLabel>Cuenta</InputLabel>
            <Select value={accountId} label="Cuenta" onChange={(e) => setAccountId(e.target.value)}>
              <MenuItem value="">Todas las cuentas</MenuItem>
              {currencyAccounts.map((account) => <MenuItem key={account.id || account._id} value={account.id || account._id}>{account.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 145 }}>
            <InputLabel>Agrupar</InputLabel>
            <Select value={bucket} label="Agrupar" onChange={(e) => setBucket(e.target.value)}>
              <MenuItem value="day">Por día</MenuItem>
              <MenuItem value="week">Por semana</MenuItem>
              <MenuItem value="month">Por mes</MenuItem>
            </Select>
          </FormControl>
          <Typography sx={{ ml: { lg: "auto" }, color: "text.secondary", fontWeight: 750, fontSize: 12.5 }}>
            Transferencias internas excluidas · una moneda por vez
          </Typography>
        </Stack>
      </Paper>

      {jarvisCategory ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          <Chip label={`Filtro de Jarvis · ${jarvisCategory}`} onDelete={clearJarvisCategory} sx={{ alignSelf: "flex-start", fontWeight: 900 }} />
          {hasJarvisRange ? <Typography sx={{ color: "text.secondary", fontSize: 12.5, fontWeight: 750 }}>Del {jarvisStart} al {jarvisEnd}</Typography> : null}
        </Stack>
      ) : null}

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,1fr)", xl: "repeat(4,1fr)" }, gap: 1.25 }}>
        <Kpi label="Ingresos" value={loading ? "—" : money(summary.income, currency)} helper={`${summary.count || 0} movimientos considerados`} icon={<TrendingUpRoundedIcon />} tone="success" />
        <Kpi label="Gastos" value={loading ? "—" : money(summary.expense, currency)} helper="Sin contar transferencias internas" icon={<TrendingDownRoundedIcon />} tone="error" />
        <Kpi label="Resultado" value={loading ? "—" : money(summary.net, currency)} helper={`Ajustes: ${money(summary.adjustments, currency)}`} icon={<BalanceRoundedIcon />} tone={Number(summary.net || 0) >= 0 ? "success" : "error"} />
        <Kpi label="Promedio por período" value={loading ? "—" : money(avgNet, currency)} helper={`Promedio neto por ${bucket === "day" ? "día" : bucket === "week" ? "semana" : "mes"} visible`} icon={<ReceiptLongRoundedIcon />} />
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 3.5, overflow: "hidden" }}>
        <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" spacing={1.2} sx={{ px: 2.25, pt: 2, pb: .6 }}>
          <Box>
            <Typography sx={{ fontWeight: 1000, fontSize: 20, letterSpacing: "-.025em" }}>Trayectoria financiera</Typography>
            <Typography sx={{ color: "text.secondary", fontWeight: 650, fontSize: 13, mt: .2 }}>Mové el cursor por la curva para leer cada período.</Typography>
          </Box>
          <ToggleButtonGroup exclusive size="small" value={metric} onChange={(_, next) => next && setMetric(next)} sx={{ alignSelf: { lg: "center" }, "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 850, px: 1.35 } }}>
            <ToggleButton value="cumulative">Acumulado</ToggleButton>
            <ToggleButton value="net">Resultado</ToggleButton>
            <ToggleButton value="income">Ingresos</ToggleButton>
            <ToggleButton value="expense">Gastos</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {loading ? <Skeleton variant="rounded" height={500} sx={{ m: 2, mt: 1 }} /> : <OwnerFinanceHistoryChart series={stats?.series || []} metric={metric} currency={currency} bucket={bucket} />}
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.35fr .65fr" }, gap: 1.4 }}>
        <Paper variant="outlined" sx={{ p: 2.1, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 1000, fontSize: 17 }}>Dónde se fue la plata</Typography>
          <Typography sx={{ mt: .25, color: "text.secondary", fontWeight: 650, fontSize: 12.5 }}>Top de categorías de gasto dentro del filtro actual.</Typography>
          <Stack spacing={1.25} sx={{ mt: 1.7 }}>
            {(stats?.topExpenseCategories || []).map((item) => (
              <Box key={item.name}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography sx={{ fontWeight: 800 }}>{item.name}</Typography>
                  <Typography sx={{ fontWeight: 950 }}>{money(item.amount, currency)}</Typography>
                </Stack>
                <Box sx={{ mt: .55, height: 7, borderRadius: 99, bgcolor: "action.hover", overflow: "hidden" }}>
                  <Box sx={{ width: `${Math.max(2, (item.amount / maxCategory) * 100)}%`, height: "100%", borderRadius: 99, bgcolor: "primary.main" }} />
                </Box>
              </Box>
            ))}
            {!loading && !(stats?.topExpenseCategories || []).length ? <Typography color="text.secondary">No hay gastos en este período.</Typography> : null}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.1, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 1000, fontSize: 17 }}>Lectura rápida</Typography>
          <Stack spacing={1.15} sx={{ mt: 1.45 }}>
            <Box>
              <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 850 }}>BALANCE DEL FILTRO</Typography>
              <Typography sx={{ mt: .25, fontWeight: 1000, fontSize: 26 }}>{money(summary.net, currency)}</Typography>
            </Box>
            <Box sx={{ pt: 1.1, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 850 }}>TASA INGRESO / GASTO</Typography>
              <Typography sx={{ mt: .25, fontWeight: 950, fontSize: 18 }}>{Number(summary.expense || 0) > 0 ? `${(Number(summary.income || 0) / Number(summary.expense || 1)).toFixed(2)}×` : "—"}</Typography>
            </Box>
            <Box sx={{ pt: 1.1, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 850 }}>ÁREA DE MAYOR GASTO</Typography>
              <Typography sx={{ mt: .25, fontWeight: 950, fontSize: 18 }}>{stats?.topExpenseAreas?.[0]?.name || "—"}</Typography>
              {stats?.topExpenseAreas?.[0] ? <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: 13 }}>{money(stats.topExpenseAreas[0].amount, currency)}</Typography> : null}
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Stack>
  );
}
